<template>
  <div class="template">
    <div class="row">
      <h4>Mes veilles</h4>
      <CreateDashboardButton />
    </div>
    <div class="row list">
      <DashboardCard
        v-if="defaultDashboard"
        :dashboard="defaultDashboard"
        is-default
        :is-custom="false"
        :is-selected="defaultDashboard._id === store.selectedDashboard?._id"
        @apply="store.applyDashboard"
      >
        Cette veille peut-être utilisée pour consulter tout le contenu de la
        plateforme sans filtres appliqués. Elle peut servir de bonne base pour
        créer une nouvelle veille.
      </DashboardCard>
      <DashboardCard
        v-for="dashboard in dashboards.sort((a, b) =>
          a.name.localeCompare(b.name)
        )"
        :key="dashboard._id"
        :dashboard="dashboard"
        :is-custom="dashboard.is_custom"
        :is-selected="dashboard._id === store.selectedDashboard?._id"
        @openUpdate="openUdpateDialog"
        @openDelete="openDeleteDialog"
        @apply="store.applyDashboard"
      />
    </div>
  </div>

  <PopupDialog ref="updateModal" title="Éditer une veille" @close="closeDialog">
    <template #body>
      <AddEditDashboardForm
        v-if="showUpdateForm"
        ref="updateForm"
        :targeted-dashboard="targetedDashboard"
        @submit="updateDashboard($event)"
      />
    </template>
    <template #footer>
      <Button v-if="showUpdateForm" @click="submitUpdate">Éditer</Button>
    </template>
  </PopupDialog>

  <PopupDialog
    ref="deleteModal"
    title="Supprimer une veille"
    @close="closeDialog"
  >
    <template #body>
      <p>Êtes-vous sûr de vouloir supprimer la veille :</p>
      <p class="center">"{{ targetedDashboard?.name }}" ?</p>
    </template>
    <template #footer>
      <Button @click="deleteDashboard">Supprimer</Button>
    </template>
  </PopupDialog>
</template>

<script setup lang="ts">
import { useTemplateRef, ref, computed } from "vue";

import CreateDashboardButton from "@follaw-fr/components/general/molecules/CreateDashboardButton.vue";
import DashboardCard from "@follaw-fr/components/general/molecules/DashboardCard.vue";
import PopupDialog from "@follaw-fr/components/reusable/atoms/PopupDialog.vue";
import Button from "@follaw-fr/components/reusable/atoms/Button.vue";
import AddEditDashboardForm from "@follaw-fr/components/general/molecules/AddEditDashboardForm.vue";
import { useDashboardsStore } from "@follaw-fr/stores/dashboards";
import type { DashboardZoomView } from "@commons/types/api/schema.types";
import { DEFAULT_DASHBOARD_ID } from "@follaw-fr/constants/dashboard.constant";

const store = useDashboardsStore();

const dashboards = computed<DashboardZoomView[]>(() => {
  return store.dashboards.filter(
    (d: DashboardZoomView) => d._id !== DEFAULT_DASHBOARD_ID
  );
});
const defaultDashboard = computed<DashboardZoomView | undefined>(() =>
  store.dashboards.find(
    (d: DashboardZoomView) => d._id === DEFAULT_DASHBOARD_ID
  )
);

const targetedDashboardId = ref<number | null>(null);
const targetedDashboard = computed<DashboardZoomView | undefined>(() =>
  dashboards.value.find(
    (d: DashboardZoomView) => d._id === targetedDashboardId.value
  )
);

// Modals

const closeDialog = () => {
  targetedDashboardId.value = null;
  showUpdateForm.value = false;
};

// UPDATE
const updateModalRef = useTemplateRef("updateModal");
const showUpdateForm = ref(false);
const openUdpateDialog = (id: number) => {
  targetedDashboardId.value = id;
  showUpdateForm.value = true;
  updateModalRef.value?.open();
};

const updateFormRef = useTemplateRef("updateForm");
const submitUpdate = () => {
  updateFormRef.value?.form?.requestSubmit();
};

const updateDashboard = (formData: DashboardZoomView) => {
  if (targetedDashboardId.value && updateFormRef.value?.validate()) {
    store.updateDashboard(targetedDashboardId.value, formData);
    deleteModalRef.value?.close();
  }
};

// DELETE
const deleteModalRef = useTemplateRef("deleteModal");
const openDeleteDialog = (id: number) => {
  targetedDashboardId.value = id;
  deleteModalRef.value?.open();
};

const deleteDashboard = () => {
  if (targetedDashboardId.value) {
    store.deleteDashboard(targetedDashboardId.value);
  }
  deleteModalRef.value?.close();
};
</script>

<style scoped>
p {
  margin: 0;
  line-height: var(--lh-normal);
}

.row {
  display: flex;
  justify-content: space-between;
  align-items: stretch;
  flex-wrap: wrap;
  gap: 16px;

  &.list {
    justify-content: flex-start;
  }
}

.center {
  text-align: center;
}
</style>
